<!-- This file contains the entire filters bar so that the filters tags can interact with the dropdown -->
<template>
  <div class="flex w-full space-x-2">
    <!-- Filter Toggle Button -->
    <button ref="filtersButton" @click.prevent="togglePopover" class="button">
      <i class="fa far fa-bars-filter"></i>
      <span>Filters {{ appliedFilterCount }}</span>
      <i class="fa far fa-chevron-down"></i>
    </button>

    <!-- Keyword Search -->
    <div v-if="showKeyword" class="flex">
      <label for="admin_filter_external_keyword" class="hidden mx-2">Keyword</label>
      <input
        type="text"
        placeholder="Search Keyword"
        class="rounded-tr-none rounded-br-none max-w-80"
        id="admin_filter_external_keyword"
        v-model="keyword"
        @keyup.enter="submitWithKeyword"
      />
      <button
        id="keyword-search-button"
        type="button"
        class="button rounded-ss-none rounded-es-none px-3"
        aria-label="Search"
        @click="submitWithKeyword">
        <i class="far fa-search m-0 text-lg"></i>
        <span class="fa5-text"></span>
      </button>
    </div>

    <!-- Filter Labels -->
    <!-- TODO: remove one layer of nesting -->
    <div class="filter-labels flex grow space-x-2 px-2" ref="filterLabels">

      <!-- NOTE for resetFilterAcrossUI -->
      <!-- Vue auto-passes emitted params: @event="handler" → @event="(param) => handler(param)" -->
      <AdminFilterLabel
        v-for="(value, key) in visibleLabels"
        :key="key"
        :filter-key="key"
        :title="labelTitle(key)"
        :value="labelValues[key] || ''"
        @openPopup="openPopup"
        @resetFilterAcrossUI="resetFilterAcrossUI">
      </AdminFilterLabel>

      <a href="#" v-if="Object.keys(visibleLabels).length > 0" class="clear-all inline-flex items-center mt-2 mr-2 mb-0 px-2 py-1" @click="resetAllFiltersAcrossUI">
        Reset Filters
      </a>
    </div>

    <!-- Filter Popover -->
    <HidablePopover ref="popover" class="popover absolute z-30 card bg-white" :dismissable="false">
      <form action="/admin/filters/set" id="filters-form" @submit.prevent="updatePage" :method="usePost ? 'post' : 'get'">

        <!-- Header -->
        <div class="flex justify-between px-6 py-4 pb-0 items-center">
          <h3>Filters</h3>
          <button type="button" class="tertiary rounded-full button w-8 h-8" @click="hidePopover">
            <i class="fa fa-times"><span class="sr-only">Cancel</span></i>
          </button>
        </div>

        <!-- Filter Content -->
        <div id="filters-content" class="filters-content bg-white rounded-md">
          <input type="hidden" name="apply_filter" value='1' />
          <input type="hidden" name="url" :value="currentPath" />
          <input type="hidden" name="filter_state_favorite" value='0' />

          <!--
          This action goes nowhere.  The filters are handled by custom calls below.
          The form tag goes outside because we need to submit all the params.
          When filters are reloaded, only the inside part of the form changes.
          -->

          <!-- THIS IS WHERE WE POPULATE WITH FILTERS -->
          <div class="filters-proper w-150">
            <slot name="filters"></slot>
          </div>

          <!-- Action Buttons -->
          <div class="submit-button-container">
            <button type="button" class="secondary button w-fit" @click="clearForm">Clear Form</button>
            <input type="submit" id="apply-filter-button" class="button primary cursor-pointer" value="Apply" />
          </div>
        </div>

        <!--
        Filter set below:
        update-filters is fired when a filter state is loaded
        filter-state-updated is fired when a filter state is saved
        -->
        <AdminFilterState
          ref="filterStateComponent"
          :current-path="currentPath"
          :is-user-superuser="isSuperuser"
          :initial-state-id="initialStateId"
          @filter-state-loaded="handleStateLoaded"
          @filter-state-saved="handleStateSaved"
        />
      </form>
    </HidablePopover>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, watch, nextTick, getCurrentInstance } from "vue"
  import AdminFilterState from './AdminFilterStates.vue'
  import AdminFilterLabel from './AdminFilterLabel.vue'
  import HidablePopover from './HidablePopover.vue'
  import { formState } from "../dependencies/formDependencies.js"
  import { initializeFormDependencies } from "../dependencies/formDependencies.js"

  const props = defineProps({
    showKeyword:    { type: Boolean, required: true },
    showFilters:    { type: Boolean, default: true },
    initialKeyword: { type: String, default: '' },
    currentPath:    { type: String, required: true },
    isSuperuser:    { type: Boolean, default: false },
    initialStateId: { type: String, default: '' },
    usePost:        { type: Boolean, default: false },
    labels:         { type: Object, default: {} }
  })

  const instance = getCurrentInstance()
  const popover = ref()
  const filterLabels = ref()
  const filterStateComponent = ref()
  const filtersButton = ref(null)
  const labelValues = ref({})

  const keyword = ref(props.initialKeyword)
  const lastClickedButton = ref(null)
  const shouldAutoApply = ref(!!props.initialStateId)

  const appliedFilterCount = computed(() => {
    if (!filterLabels.value) return ''

    const filters = Array.from(filterLabels.value.children).filter(el => !el.classList.contains('clear-all'))

    return filters.length === 0 ? '' : `(${filters.length})`
  })

  const togglePopover = (event) => popover.value.toggle(event)
  const hidePopover = (event) => popover.value.hide(event)

  const visibleLabels = computed(() => {
    if (formState.loading) return {}
    if (!filterLabels.value) return {}

    const form = document.getElementById('filters-form')
    const filterContent = form.querySelector('.filters-proper')

    const visible = {}

    Object.keys(props.labels).forEach(key => {
      const label = filterContent.querySelector(`label[for*="admin_filter_${key}"]`)
      if (!label) return

      const formRow = label.closest('.form-row')
      if (!formRow) return

      const isHidden = formRow.classList.contains('hidden') || formRow.classList.contains('!hidden')

      if (!isHidden) {
        visible[key] = props.labels[key]
      }
    })

    return visible
  })

  // Resets a single form element to its default/empty state
  // Handles different element types: selects, multiselects, and PrimeVue components
  const resetFilterElement = (elementAndType) => {
    const { el, type } = elementAndType

    if (!el) return

    if (type == 'datetime') {
      el.dispatchEvent(new CustomEvent('update:modelValue', {
        detail: '',
        bubbles: true
      }))
    } else if (type == 'multiselect') {
      // Dispatch custom event to clear multiselect via Vue component
      el.dispatchEvent(new CustomEvent('update:modelValue', {
        detail: [],
        bubbles: true
      }))

      // // Handle PrimeVue AutoComplete chip removal
      // if (el.querySelector('[data-pc-section="inputmultiple"]')) {
      //   const removeIcons = el.querySelector('[data-pc-section="inputmultiple"]').querySelectorAll('[data-pc-section="removeicon"]')
      //   for (const icon of removeIcons) {
      //     icon.dispatchEvent(new MouseEvent('click', { bubbles: true }))
      //   }
      // }
    } else if (el.matches('select')) {
      el.value = ''
    }
  }

  // Resets all filters when "Reset Filters" link is clicked
  // This fires on the filter label close click
  function resetAllFiltersAcrossUI() {
    const form = document.getElementById('filters-form')
    if (!form) return

    const filterContent = form.querySelector('.filters-proper')
    if (!filterContent) return

    // Clear all standard form inputs
    filterContent.querySelectorAll('select').forEach(select => { select.value = '' })
    filterContent.querySelectorAll('input[type="text"], textarea').forEach(input => { input.value = '' })
    filterContent.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(input => { input.checked = false })

    // Clear PrimeVue AutoComplete chips
    filterContent.querySelectorAll('[data-pc-section="inputmultiple"]').forEach(section => {
      section.querySelectorAll('[data-pc-section="removeicon"]').forEach(icon => {
        icon.dispatchEvent(new MouseEvent('click', { bubbles: false }))
      })
    })

    // Clear custom multiselect components
    filterContent.querySelectorAll('.multiselect, .grouped-multiselect').forEach(parentEl => {
      parentEl.dispatchEvent(new CustomEvent('update:modelValue', { detail: [], bubbles: false }))
    })

    resetKeywordFilter()

    // Clear any saved filter state
    filterStateComponent.value?.clearSelectedState()

    // Refresh the page with cleared filters
    updatePage()
  }

  const resetKeywordFilter = () => {
    keyword.value = ''

    const externalInput = document.querySelector('#admin_filter_external_keyword')
    const internalInput = document.querySelector('#admin_filter_keyword')
    if (externalInput) externalInput.value = ''
    if (internalInput) internalInput.value = ''

    updatePage()
  }

  // Called by AdminFilterLabel when user clicks the X button on a filter label
  // Flow:
  //   1. filterKey passed as prop to AdminFilterLabel (:filter-key="key")
  //   2. User clicks X button in AdminFilterLabel
  //   3. AdminFilterLabel emits filterKey via @resetFilterAcrossUI event
  //   4. This function receives filterKey and clears the filter
  function resetFilterAcrossUI(filterKey) {
    // Handle keyword filter internal/external inputs
    if (filterKey.includes('keyword')) {
      resetKeywordFilter()
    } else {
      // Find the element and reset its value
      resetFilterElement(keyToElementAndType(filterKey))

      // Refresh the page with updated filters
      updatePage()
    }
  }

  const resetInvisibleFilters = async () => {
    const form = document.getElementById('filters-form')
    if (!form) return

    const filterContent = form.querySelector('.filters-proper')
    if (!filterContent) return

    Object.keys(props.labels).forEach(key => {
      const label = filterContent.querySelector(`label[for*="admin_filter_${key}"]`)
      if (!label) return

      const formRow = label.closest('.form-row')
      if (!formRow) return

      const isHidden = formRow.classList.contains('hidden') || formRow.classList.contains('!hidden')

      if (isHidden) {
        resetFilterElement(keyToElementAndType(key))
      }
    })

    await nextTick()
  }

  // This clears all the fields back to default or '' when the Clear Form button is clicked
  const clearForm = (event) => {
    const filterContent = event.target.closest('form').querySelector('.filters-proper')
    if (!filterContent) return

    // Clear custom multiselect components
    filterContent.querySelectorAll('.multiselect, .grouped-multiselect').forEach(parentEl => {
      parentEl.dispatchEvent(new CustomEvent('update:modelValue', {
        detail: [],
        bubbles: true
      }))
    })

    // Clear keyword input and other visible inputs
    filterContent.querySelectorAll('input[type="text"], textarea, select:not(.multiselect select):not(.grouped-multiselect select)').forEach(input => {
      input.value = ''
    })

    // Reset any checkboxes/radios
    filterContent.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(el => {
      el.checked = false
    })

    // Clear any saved filter state
    filterStateComponent.value?.clearSelectedState()
  }

  // Submits the filter form and updates the page with filtered results
  const updatePage = async () => {
    // TODO: Replace different sections with loading icon
    // $('.summary').html('<%=j center_note(spinning_dots_icon + " Loading...".html_safe) %>')
    // $('.items-table-wrapper').html('<%=j center_note(spinning_dots_icon + " Loading...".html_safe) %>')
    // $('#results').html('<%=j center_note(spinning_dots_icon + " Loading...".html_safe) %>')

    // TODO: Override uquery page so it doesn't display loading
    // $('#results.uquery-results').html('<%=j center_note("No query has been run yet.") %>')
    await resetInvisibleFilters()

    // Determines which input field to use based on the submit button clicked (including empty values)
    const activeElement = document.activeElement

    if (activeElement?.id === 'keyword-search-button') {
      lastClickedButton.value = 'keyword-search'
    } else if (activeElement?.id === 'apply-filter-button') {
      lastClickedButton.value = 'apply-filter'
    }

    const internalInput = document.querySelector('#admin_filter_keyword')
    const externalInput = document.querySelector('#admin_filter_external_keyword')

    let finalKeywordValue = ''

    const internalValue = internalInput ? internalInput.value.trim() : ''
    const externalValue = externalInput ? externalInput.value.trim() : ''

    // Use value based on which button was clicked
    if (lastClickedButton.value === 'keyword-search') {
      finalKeywordValue = externalValue
    } else if (lastClickedButton.value === 'apply-filter') {
      finalKeywordValue = internalValue
    } else {
      finalKeywordValue = internalValue || externalValue
    }

    // Sync keyword value across both inputs
    keyword.value = finalKeywordValue

    if (internalInput) {
      internalInput.value = finalKeywordValue
    }

    if (externalInput) {
      externalInput.value = finalKeywordValue
    }

    // Build form data and make request
    const formData = new URLSearchParams(new FormData(document.getElementById('filters-form')))
    const baseUrl = window.location.href.split(/\?|#/)[0]

    const makeRequest = async (url, options) => {
      try {
        const response = await fetch(url, options)
        const script = await response.text()
        eval(script)
      } catch (error) {
        console.error('Request failed:', error)
      }
    }

    const baseOptions = {
      credentials: 'same-origin',
      headers: {
        'Accept': 'text/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }

    // 2025: as of now only uid_uquiries controller passes usePost as true
    if (props.usePost) {
      makeRequest(baseUrl, {
        method: 'POST',
        ...baseOptions,
        headers: {
          ...baseOptions.headers,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      })
    } else {
      makeRequest(`${baseUrl}?${formData.toString()}`, {
        method: 'GET',
        ...baseOptions
      })
    }

    hidePopover()
  }

  // Submit filters when user presses Enter in keyword search
  const submitWithKeyword = () => {
    lastClickedButton.value = 'keyword-search'
    updatePage()
  }

  // Example of the filter label:
  // [ Title: Value  x ]
  // [ Sessions: Students Fall 2015 (Search)  x ]

  // Returns the title for a filter label (e.g., "Sessions")
  // Finds the "Sessions" label inside the popover: label[for*="admin_filter_sessions_id"]
  const labelTitle = (key) => {
    return document.querySelector(`label[for*="admin_filter_${key}"]`)?.innerHTML.trim() || ""
  }

  // Returns the current selected value(s) for a filter to display in the filter label
  // Handles different filter types: multiselects, regular selects, text inputs, textareas
  const labelValue = async (key) => {
    await nextTick()

    const { el, type } = keyToElementAndType(key) || {}
    if (!el) return ''

    if (type == 'datetime') {
      return el.value
    } else if (type == 'multiselect') {
      const chipItems = el.querySelectorAll('[data-pc-section="chipitem"]')
      return Array.from(chipItems).map(chip => chip.innerText || chip.textContent).join(", ")
    } else if (type == 'standard') {
      if (el.tagName === "SELECT") {
        return el.options[el.selectedIndex]?.text || ''
      } else if (el.tagName == "TEXTAREA") {
        return el.value.slice(0, 50)
      }
      return el.value || ''
    }
    return ''
  }

  // Helper to focus an element (handles both simple and complex inputs)
  // Returns true if focus was successful, false otherwise
  const focusElement = (element) => {
    if (!element) return false

    // For simple inputs, selects, textareas - focus directly
    if (element.matches('input, select, textarea')) {
      element.focus()
      return true
    }

    // For complex components (like multiselects) - find the input inside
    const input = element.querySelector('[data-pc-section="input"]') || 
                  element.querySelector('input[type="text"]')
    if (input) {
      input.focus()
      return true
    }

    return false
  }

  // Called inside openPopup
  // Example: if [ Sessions: Students Fall 2015 (Search)  x ] is clicked, the popover will open, find the sessions element and focus on it
  function focusElementInsidePopover(filterKey) {
    const { el } = keyToElementAndType(filterKey) || {}
    return focusElement(el)
  }

  // This opens the popup when a filter label is clicked
  // Converts the display name back to the filter key, then focuses the corresponding input
  const openPopup = (displayTitle) => {
    // Find the key that matches this display title
    // Example: displayTitle = "User session statuses" → matchingKey = "user_session_statuses"
    const matchingKey = Object.keys(props.labels).find(key => {
      const title = labelTitle(key)
      return title === displayTitle
    })

    // Open the filter popup
    if (filtersButton.value) {
      filtersButton.value.click()
    }

    if (matchingKey) {
      // Wait for popup to fully render, then focus on the element which matches the filter label clicked
      requestAnimationFrame(() => {
        focusElementInsidePopover(matchingKey)
      })
    }
  }

  // Helper function to find the filter input/select/keyword element inside the popover
  const keyToElementAndType = (elementName) => {
    let elName = document.getElementsByName(elementName)[0]
    if (!elName) {
      elName = document.getElementsByName(elementName + '[]')[0]
      if (!elName) return { el: null }
    }

    if (elName.previousElementSibling?.matches('[data-pc-name="datepicker"]')) {
      return { el: elName.previousElementSibling.querySelector('input[type="text"]'), type: 'datetime' }
    } else if (elName.nextElementSibling?.matches('.multiselect')) {
      // Since multiselects are preceded by a hidden input with the same name
      return { el: elName.nextElementSibling, type: 'multiselect' }
    } else if (elName.closest('.grouped-multiselect')) {
      return { el: elName.closest('.grouped-multiselect'), type: 'multiselect' }
    } else if (elName.matches('select') || elName.type === 'text' || elName.type === 'textarea') {
      return { el: elName, type: 'standard' }
    }
  }

  // Populates form elements with saved filter state data
  // Used when loading a saved filter state
  const populateSavedFilterElements = (data) => {
    Object.entries(data).forEach(([elementName, value]) => {
      // console.log(elementName)
      const { el, type } = keyToElementAndType(elementName)
      if (!el) return

      if (type == 'datetime') {
        nextTick(() => {
          el.dispatchEvent(new CustomEvent('update:modelValue', {
            detail: value,
            bubbles: true
          }))
        })
      } else if (type == 'multiselect') {
        nextTick(() => {
          const normalizedValue = Array.isArray(value) ? value : [value]

          // Dispatch custom event to clear multiselect via Vue component
          el.dispatchEvent(new CustomEvent('update:modelValue', {
            detail: normalizedValue,
            bubbles: true
          }))
          // console.log('AF: updating multiselect models with values')
        })
      } else if (type == 'standard') {
        if (value) {
          // el.value is the current DOM element's value so we can use that if value = ""
          el.value = value
        }
      }
    })
  }

  // Called when a saved filter state is loaded
  // Populates all filter fields with the saved values
  const handleStateLoaded = async (filterData) => {
    // console.log('AF: state loaded detected.  filterData: ', filterData)
    // Remove internal metadata keys before populating
    const keysToRemove = ['apply_filter', 'filter_state_favorite', 'filter_state_name']
    keysToRemove.forEach(key => delete filterData[key])

    // We do need this because initializeFormDependencies only act on things with explicit dependencies
    populateSavedFilterElements(filterData)

    // Update keyword if present in saved state
    if (filterData.keyword) {
      keyword.value = filterData.keyword
    }

    await nextTick()

    // This will cascade through all levels AND restore saved values automatically
    if (instance?.proxy?.$initializeFormDependencies) {
      await instance.proxy.$initializeFormDependencies()
    }

    if (shouldAutoApply.value) {
      shouldAutoApply.value = false
      await nextTick()
      document.getElementById('apply-filter-button')?.click()
    }
  }

  // Called when a filter state is saved
  const handleStateSaved = () => {
    // Handle state updates
    // Nothing should happen when the filter state is saved
  }

  // Watch for changes to labels prop and update displayed values
  // This keeps the filter label values in sync with the actual form state
  watch(() => props.labels, async (newLabels) => {
    for (const key in newLabels) {
      labelValues.value[key] = await labelValue(key)
    }
  }, { immediate: true, deep: true })

  onMounted(() => {
    // No event listeners needed - detection happens in updatePage()
  })

  onUnmounted(() => {
  })
</script>
